import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import StatsOverview from "@/components/dashboard/StatsOverview";
import BookingsView from "@/components/dashboard/BookingsView";
import InquiriesTable from "@/components/dashboard/InquiriesTable";
import CustomersTable from "@/components/dashboard/CustomersTable";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Database, LogOut } from "lucide-react";

export default function Dashboard() {
  const [refreshKey, setRefreshKey] = useState(0);
  const { logout } = useAuth();
  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/stats");
        if (res.data.total_bookings === 0 && res.data.new_inquiries === 0) {
          await api.post("/seed");
          triggerRefresh();
        }
      } catch {}
    })();
  }, []);

  const reseed = async () => {
    try {
      await api.post("/seed");
      toast.success("Demo data reseeded");
      triggerRefresh();
    } catch { toast.error("Seed failed"); }
  };

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out");
  };

  return (
    <div className="min-h-screen cream-bg">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c97b3a]">Operations</span>
            <h1 className="font-display text-4xl md:text-5xl text-[#1f2e1f] mt-2">Trailhead Dashboard</h1>
            <p className="text-[#5a5245] mt-2">Bookings, inquiries, customers — all your barrel taps in one place.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={reseed} variant="outline" className="border-[#1f2e1f] text-[#1f2e1f] hover:bg-[#1f2e1f] hover:text-white rounded-full" data-testid="reseed-button">
              <Database className="mr-2 h-4 w-4" /> Reseed demo data
            </Button>
            <Button onClick={handleLogout} variant="outline" className="border-[#a34343] text-[#a34343] hover:bg-[#a34343] hover:text-white rounded-full" data-testid="logout-button">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-[#e8dcc4] border border-[#dcd3c6] p-1 rounded-full mb-6" data-testid="dashboard-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full data-[state=active]:bg-[#1f2e1f] data-[state=active]:text-[#F8F5F0] px-5 py-2">Overview</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-full data-[state=active]:bg-[#1f2e1f] data-[state=active]:text-[#F8F5F0] px-5 py-2">Bookings</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="rounded-full data-[state=active]:bg-[#1f2e1f] data-[state=active]:text-[#F8F5F0] px-5 py-2">Inquiries</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-full data-[state=active]:bg-[#1f2e1f] data-[state=active]:text-[#F8F5F0] px-5 py-2">Customers</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><StatsOverview refreshKey={refreshKey} /></TabsContent>
          <TabsContent value="bookings"><BookingsView refreshKey={refreshKey} onChange={triggerRefresh} /></TabsContent>
          <TabsContent value="inquiries"><InquiriesTable refreshKey={refreshKey} onChange={triggerRefresh} /></TabsContent>
          <TabsContent value="customers"><CustomersTable refreshKey={refreshKey} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
